import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import moment from "moment"
import { RootState } from ".."
import {
    LocationChartsState,
    MutationTypes,
    ActionTypes,
    ChartSettings,
    MeasurementEntry,
    ForecastEntry,
    CategoryCard,
    TooltipSyncState,
} from "./types"
import { ChartDataDisplay, ChartDataInterval, ChartDataType } from "@/types/data"
import { CATEGORY_ENUMERATIONS } from "@/types/category"

const state: LocationChartsState = {
    categories: [],
    supportedCategories: [],
    categoryCards: [],
    measurements: [],
    forecast: [],
    isLoading: false,
    error: null,
    settings: {
        dataType: ChartDataType.POWER,
        dataInterval: ChartDataInterval.QUARTER_HOUR,
        dataDisplay: ChartDataDisplay.COMBINE,
        from: null,
        to: null,
    },
    abortController: null,
    tooltipSync: {
        timestamp: null,
        isActive: false,
        sourceChartId: null,
    },
}

const mutations: MutationTree<LocationChartsState> = {
    [MutationTypes.SET_CATEGORIES](state, categories: string[]) {
        state.categories = categories
    },
    [MutationTypes.SET_SUPPORTED_CATEGORIES](state, supportedCategories: string[]) {
        state.supportedCategories = supportedCategories
    },
    [MutationTypes.SET_CATEGORY_CARDS](state, categoryCards: CategoryCard[]) {
        state.categoryCards = categoryCards
    },
    [MutationTypes.SET_MEASUREMENTS](state, measurements: MeasurementEntry[]) {
        state.measurements = measurements
    },
    [MutationTypes.SET_FORECAST](state, forecast: ForecastEntry[]) {
        state.forecast = forecast
    },
    [MutationTypes.SET_LOADING](state, isLoading: boolean) {
        state.isLoading = isLoading
    },
    [MutationTypes.SET_ERROR](state, error: string | null) {
        state.error = error
    },
    [MutationTypes.SET_SETTINGS](state, settings: ChartSettings) {
        state.settings = settings
    },
    [MutationTypes.SET_ABORT_CONTROLLER](state, abortController: AbortController | null) {
        state.abortController = abortController
    },
    [MutationTypes.SET_TOOLTIP_SYNC](state, tooltipSync: TooltipSyncState) {
        state.tooltipSync = tooltipSync
    },
    [MutationTypes.CLEAR_TOOLTIP_SYNC](state) {
        state.tooltipSync = {
            timestamp: null,
            isActive: false,
            sourceChartId: null,
        }
    },
}

const actions: ActionTree<LocationChartsState, RootState> = {
    async [ActionTypes.FETCH_CHART_DATA]({ commit, state }, { locationId }) {
        if (state.abortController) {
            state.abortController.abort()
        }

        const abortController = new AbortController()
        commit(MutationTypes.SET_ABORT_CONTROLLER, abortController)
        commit(MutationTypes.SET_LOADING, true)
        commit(MutationTypes.SET_ERROR, null)

        try {
            const response = await axios.get(`/locations/${locationId}/data/charts`, {
                signal: abortController.signal,
                params: {
                    from: moment(state.settings.from).format("YYYY-MM-DD"),
                    to: moment(state.settings.to).format("YYYY-MM-DD"),
                    type: state.settings.dataType,
                    interval: state.settings.dataInterval,
                },
            })

            const currentTime = moment().valueOf()
            const measurements = response.data.data.measurements.filter(
                (entry: MeasurementEntry) => entry.time <= currentTime,
            )
            const forecast = response.data.data.forecast || []

            commit(MutationTypes.SET_MEASUREMENTS, measurements)
            commit(MutationTypes.SET_FORECAST, forecast)
            commit(MutationTypes.SET_SUPPORTED_CATEGORIES, response.data.data.supportedCategories)
            commit(MutationTypes.SET_CATEGORY_CARDS, response.data.data.cards)

            if (state.categories.length === 0) {
                commit(MutationTypes.SET_CATEGORIES, response.data.data.supportedCategories)
            }
        } catch (error: any) {
            if (axios.isCancel(error)) {
                return
            }
            commit(MutationTypes.SET_ERROR, error.message || "Failed to fetch chart data")
        } finally {
            commit(MutationTypes.SET_LOADING, false)
            commit(MutationTypes.SET_ABORT_CONTROLLER, null)
        }
    },

    [ActionTypes.UPDATE_CATEGORIES]({ commit }, categories: string[]) {
        commit(MutationTypes.SET_CATEGORIES, categories)
    },

    [ActionTypes.UPDATE_SETTINGS]({ commit }, settings: ChartSettings) {
        commit(MutationTypes.SET_SETTINGS, settings)
    },

    [ActionTypes.ABORT_FETCH]({ state, commit }) {
        if (state.abortController) {
            state.abortController.abort()
            commit(MutationTypes.SET_ABORT_CONTROLLER, null)
        }
    },

    [ActionTypes.UPDATE_TOOLTIP_POSITION](
        { commit },
        payload: { timestamp: number; sourceChartId: string },
    ) {
        commit(MutationTypes.SET_TOOLTIP_SYNC, {
            timestamp: payload.timestamp,
            isActive: true,
            sourceChartId: payload.sourceChartId,
        })
    },

    [ActionTypes.CLEAR_TOOLTIP]({ commit }) {
        commit(MutationTypes.CLEAR_TOOLTIP_SYNC)
    },
}

const getters: GetterTree<LocationChartsState, RootState> = {
    categories: (state) => state.categories,
    supportedCategories: (state) => state.supportedCategories,
    categoryCards: (state) => state.categoryCards,
    measurements: (state) => state.measurements,
    forecast: (state) => state.forecast,
    isLoading: (state) => state.isLoading,
    error: (state) => state.error,
    settings: (state) => state.settings,

    activeCategories: (state) => {
        return state.categoryCards.filter((item) => !item.hidden && item.category)
    },

    totalCategories: (state) => {
        return state.categoryCards.filter(
            (item) => !item.hidden && ["PRODUCTION", "CONSUMPTION"].includes(item.key),
        )
    },

    isBatteryAvailable: (state) => {
        return state.categories.some((cat) => cat === CATEGORY_ENUMERATIONS.BATTERY)
    },

    cards: (state) => {
        return state.categoryCards.filter(
            (item) =>
                item.category === null || (item.isAvailable && state.categories.includes(item.key)),
        )
    },

    tooltipSync: (state) => state.tooltipSync,
}

const locationChartsModule: Module<LocationChartsState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default locationChartsModule
